// Real-time Notifications Service using WebSocket
// Handles live notification delivery and UI updates

interface Notification {
  id: string
  type: 'like' | 'comment' | 'repost' | 'follow' | 'mention' | 'reply'
  userId: string
  userName: string
  userAvatar?: string
  postId?: string
  content?: string
  timestamp: string
  read: boolean
}

type NotificationCallback = (notification: Notification) => void
type ConnectionCallback = (connected: boolean) => void

class NotificationService {
  private ws: WebSocket | null = null
  private wsUrl: string
  private reconnectAttempts = 0
  private maxReconnectAttempts = 5
  private reconnectDelay = 3000
  private notificationCallbacks: NotificationCallback[] = []
  private connectionCallbacks: ConnectionCallback[] = []
  private notifications: Notification[] = []
  private unreadCount = 0
  private authToken: string | null = null
  private reconnectTimeout: NodeJS.Timeout | null = null

  constructor() {
    // WebSocket URL from environment or default
    // For now, using a placeholder - will be configured when backend WebSocket is ready
    // WebSocket disabled — no server exists yet. Using localStorage for notifications.
    this.wsUrl = import.meta.env.VITE_WS_URL || ''

    // Load cached notifications from localStorage
    this.loadCachedNotifications()
  }

  // ============= Connection Management =============

  async connect() {
    try {
      // Skip if no WebSocket URL configured
      if (!this.wsUrl) return

      // Get auth token
      this.authToken = localStorage.getItem('auth_token')

      if (!this.authToken) {
        return
      }

      // Close existing connection if any
      if (this.ws) {
        this.ws.close()
      }

      console.log('🔌 Connecting to notification WebSocket:', this.wsUrl)

      // Create WebSocket connection with auth token
      const wsUrlWithAuth = `${this.wsUrl}?token=${encodeURIComponent(this.authToken)}`
      this.ws = new WebSocket(wsUrlWithAuth)

      this.ws.onopen = this.handleOpen.bind(this)
      this.ws.onmessage = this.handleMessage.bind(this)
      this.ws.onerror = this.handleError.bind(this)
      this.ws.onclose = this.handleClose.bind(this)

    } catch (error) {
      console.error('❌ WebSocket connection error:', error)
      this.scheduleReconnect()
    }
  }

  disconnect() {
    console.log('🔌 Disconnecting WebSocket')

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout)
      this.reconnectTimeout = null
    }

    if (this.ws) {
      this.ws.close()
      this.ws = null
    }

    this.notifyConnectionCallbacks(false)
  }

  private handleOpen() {
    console.log('✅ WebSocket connected')
    this.reconnectAttempts = 0
    this.notifyConnectionCallbacks(true)

    // Send ping to keep connection alive
    this.startHeartbeat()
  }

  private handleMessage(event: MessageEvent) {
    try {
      const data = JSON.parse(event.data)

      // Handle different message types
      switch (data.type) {
        case 'notification':
          this.handleNotification(data.notification)
          break
        case 'pong':
          // Heartbeat response
          break
        case 'batch':
          // Handle batch notifications
          if (Array.isArray(data.notifications)) {
            data.notifications.forEach((notif: Notification) => {
              this.handleNotification(notif)
            })
          }
          break
        default:
          console.log('📨 Unknown message type:', data.type)
      }
    } catch (error) {
      console.error('❌ Error parsing WebSocket message:', error)
    }
  }

  private handleError(error: Event) {
    console.error('❌ WebSocket error:', error)
  }

  private handleClose(event: CloseEvent) {
    console.log('🔌 WebSocket closed:', event.code, event.reason)
    this.ws = null
    this.notifyConnectionCallbacks(false)

    // Attempt reconnection if not a normal closure
    if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
      this.scheduleReconnect()
    }
  }

  private scheduleReconnect() {
    this.reconnectAttempts++
    const delay = this.reconnectDelay * this.reconnectAttempts

    console.log(`🔄 Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`)

    this.reconnectTimeout = setTimeout(() => {
      this.connect()
    }, delay)
  }

  private heartbeatInterval: NodeJS.Timeout | null = null

  private startHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval)
    }

    this.heartbeatInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'ping' }))
      }
    }, 30000) // Ping every 30 seconds
  }

  // ============= Notification Handling =============

  private handleNotification(notification: Notification) {
    console.log('🔔 New notification:', notification)

    // Add to notifications list
    this.notifications.unshift(notification)

    // Update unread count
    if (!notification.read) {
      this.unreadCount++
    }

    // Cache notifications
    this.cacheNotifications()

    // Notify all callbacks
    this.notificationCallbacks.forEach(callback => {
      try {
        callback(notification)
      } catch (error) {
        console.error('Error in notification callback:', error)
      }
    })

    // Show browser notification if permission granted
    this.showBrowserNotification(notification)
  }

  private showBrowserNotification(notification: Notification) {
    if ('Notification' in window && Notification.permission === 'granted') {
      const title = this.getNotificationTitle(notification)
      const body = this.getNotificationBody(notification)

      new Notification(title, {
        body,
        icon: notification.userAvatar || '/logo.svg',
        badge: '/logo.svg',
        tag: notification.id
      })
    }
  }

  private getNotificationTitle(notification: Notification): string {
    switch (notification.type) {
      case 'like':
        return '❤️ New Like'
      case 'comment':
        return '💬 New Comment'
      case 'repost':
        return '🔄 New Repost'
      case 'follow':
        return '👥 New Follower'
      case 'mention':
        return '@ Mention'
      case 'reply':
        return '💬 Reply'
      default:
        return '🔔 Notification'
    }
  }

  private getNotificationBody(notification: Notification): string {
    switch (notification.type) {
      case 'like':
        return `${notification.userName} liked your post`
      case 'comment':
        return `${notification.userName} commented: ${notification.content?.substring(0, 50)}...`
      case 'repost':
        return `${notification.userName} reposted your post`
      case 'follow':
        return `${notification.userName} started following you`
      case 'mention':
        return `${notification.userName} mentioned you`
      case 'reply':
        return `${notification.userName} replied: ${notification.content?.substring(0, 50)}...`
      default:
        return notification.content || 'New notification'
    }
  }

  // ============= Public API =============

  onNotification(callback: NotificationCallback) {
    this.notificationCallbacks.push(callback)

    // Return unsubscribe function
    return () => {
      const index = this.notificationCallbacks.indexOf(callback)
      if (index > -1) {
        this.notificationCallbacks.splice(index, 1)
      }
    }
  }

  onConnectionChange(callback: ConnectionCallback) {
    this.connectionCallbacks.push(callback)

    // Immediately call with current state
    callback(this.ws?.readyState === WebSocket.OPEN)

    // Return unsubscribe function
    return () => {
      const index = this.connectionCallbacks.indexOf(callback)
      if (index > -1) {
        this.connectionCallbacks.splice(index, 1)
      }
    }
  }

  private notifyConnectionCallbacks(connected: boolean) {
    this.connectionCallbacks.forEach(callback => {
      try {
        callback(connected)
      } catch (error) {
        console.error('Error in connection callback:', error)
      }
    })
  }

  getNotifications(): Notification[] {
    return this.notifications
  }

  getUnreadCount(): number {
    return this.unreadCount
  }

  markAsRead(notificationId: string) {
    const notification = this.notifications.find(n => n.id === notificationId)
    if (notification && !notification.read) {
      notification.read = true
      this.unreadCount = Math.max(0, this.unreadCount - 1)
      this.cacheNotifications()

      // Send to backend
      this.sendReadStatus(notificationId)
    }
  }

  markAllAsRead() {
    this.notifications.forEach(notification => {
      notification.read = true
    })
    this.unreadCount = 0
    this.cacheNotifications()

    // Send to backend
    this.sendMarkAllRead()
  }

  clearAll() {
    this.notifications = []
    this.unreadCount = 0
    this.cacheNotifications()
  }

  // ============= Browser Notifications Permission =============

  async requestPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
      console.log('⚠️ Browser notifications not supported')
      return false
    }

    if (Notification.permission === 'granted') {
      return true
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission()
      return permission === 'granted'
    }

    return false
  }

  // ============= Backend Communication =============

  private async sendReadStatus(notificationId: string) {
    try {
      // Send via WebSocket if connected
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({
          type: 'mark_read',
          notificationId
        }))
      }
    } catch (error) {
      console.error('Error sending read status:', error)
    }
  }

  private async sendMarkAllRead() {
    try {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({
          type: 'mark_all_read'
        }))
      }
    } catch (error) {
      console.error('Error marking all as read:', error)
    }
  }

  // ============= Local Storage Cache =============

  private cacheNotifications() {
    try {
      localStorage.setItem('notifications', JSON.stringify({
        notifications: this.notifications.slice(0, 50), // Keep last 50
        unreadCount: this.unreadCount,
        timestamp: Date.now()
      }))
    } catch (error) {
      console.error('Error caching notifications:', error)
    }
  }

  private loadCachedNotifications() {
    try {
      const cached = localStorage.getItem('notifications')
      if (cached) {
        const data = JSON.parse(cached)

        // Only load if less than 24 hours old
        if (Date.now() - data.timestamp < 24 * 60 * 60 * 1000) {
          this.notifications = data.notifications || []
          this.unreadCount = data.unreadCount || 0
          console.log(`✅ Loaded ${this.notifications.length} cached notifications`)
        }
      }
    } catch (error) {
      console.error('Error loading cached notifications:', error)
    }
  }

  // ============= Demo Notifications (for testing) =============

  simulateNotification(type: Notification['type'] = 'like') {
    const demoNotification: Notification = {
      id: `demo_${Date.now()}`,
      type,
      userId: 'demo_user',
      userName: 'Demo User',
      userAvatar: '/logo.svg',
      postId: 'demo_post',
      content: 'This is a demo notification',
      timestamp: new Date().toISOString(),
      read: false
    }

    this.handleNotification(demoNotification)
  }
}

// Export singleton instance
export const notificationService = new NotificationService()
export default notificationService
